import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, AlertTriangle, Download, Upload, Eye } from 'lucide-react';
import { SearchFilters, TruckStatus, Truck } from '../../types';
import { useAppContext } from '../../contexts/AppContext';
import { TruckForm } from '../forms/TruckForm';
import { SearchAndFilter } from '../common/SearchAndFilter';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { downloadTextFile, getCsvCell, makeImportId, parseCsvRecords, parseNumber, toCsv } from '../../utils/dataExchange';
import { formatDistance, getDistanceUnitLabel, getMileageCsvHeader, readMileageFromCsvRecord, toDisplayDistance, useDistanceUnit } from '../../utils/distance';
import '../../styles/enhanced.css';

const truckStatuses: TruckStatus[] = ['In Service', 'Out for Repair', 'Needs Attention', 'Retired'];

const normalizeTruckStatus = (value: string): TruckStatus => {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, '');
  const aliases: Record<string, TruckStatus> = {
    active: 'In Service',
    inservice: 'In Service',
    available: 'In Service',
    operating: 'In Service',
    maintenance: 'Out for Repair',
    repair: 'Out for Repair',
    outforrepair: 'Out for Repair',
    outofservice: 'Out for Repair',
    needsattention: 'Needs Attention',
    warning: 'Needs Attention',
    retired: 'Retired',
    inactive: 'Retired'
  };

  return aliases[normalized] || truckStatuses.find(status => status.toLowerCase() === value.toLowerCase()) || 'In Service';
};

const EnhancedTrucksPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, addTruck, updateTruck, deleteTruck } = useAppContext();
  const { trucks } = state;
  const distanceUnit = useDistanceUnit();
  const importInputRef = useRef<HTMLInputElement>(null);
  
  const [filteredTrucks, setFilteredTrucks] = useState<Truck[]>([]);
  const [paginatedTrucks, setPaginatedTrucks] = useState<Truck[]>([]);
  const [showTruckForm, setShowTruckForm] = useState(false);
  const [editingTruck, setEditingTruck] = useState<Truck | undefined>();
  const [selectedTrucks, setSelectedTrucks] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [deviceHealthMap, setDeviceHealthMap] = useState<Record<string, { alertStatus: string; healthScore: number; deviceId?: string; createdAt?: Date | string }>>({});
  const [linkedDeviceMap, setLinkedDeviceMap] = useState<Record<string, string>>({});
  const [importFeedback, setImportFeedback] = useState<{ tone: 'success' | 'warning' | 'danger'; text: string; details?: string[] } | null>(null);
  const totalPages = Math.ceil(filteredTrucks.length / itemsPerPage);

  const applyFilters = useCallback(() => {
    let filtered = state.trucks;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
  filtered = filtered.filter((truck: Truck) => 
        truck.licensePlate?.toLowerCase().includes(query) ||
        truck.model?.toLowerCase().includes(query) ||
        truck.make?.toLowerCase().includes(query) ||
        truck.nickname?.toLowerCase().includes(query)
      );
    }

    if (filters.status && filters.status.length > 0) {
  filtered = filtered.filter((truck: Truck) => 
        filters.status!.includes(truck.status as TruckStatus)
      );
    }

    if (filters.dateRange) {

    }

    setFilteredTrucks(filtered);
    

    setCurrentPage(1);
  }, [state.trucks, searchQuery, filters]);


  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    const loadDeviceHealth = async () => {
      try {
        const linkSnapshot = await getDocs(collection(db, 'deviceLinks'));
        const truckLinks: Record<string, string> = {};
        const deviceIds: string[] = [];

        linkSnapshot.docs.forEach((doc) => {
          const data = doc.data() as any;
          if (data.truckId && data.deviceId) {
            truckLinks[data.truckId] = data.deviceId;
            deviceIds.push(data.deviceId);
          }
        });

        setLinkedDeviceMap(truckLinks);

        if (deviceIds.length === 0) {
          setDeviceHealthMap({});
          return;
        }

        const readingSnapshot = await getDocs(
          query(
            collection(db, 'esp32Readings'),
            orderBy('createdAt', 'desc'),
            limit(100)
          )
        );

        const latestByDevice: Record<string, any> = {};

        readingSnapshot.docs.forEach((doc) => {
          const data = doc.data() as any;
          const id = data.deviceId;
          if (id && !latestByDevice[id]) {
            latestByDevice[id] = {
              healthScore: data.healthScore || 0,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
              deviceId: id
            };
          }
        });

        const healthMap: Record<string, { alertStatus: string; healthScore: number; deviceId?: string; createdAt?: Date | string }> = {};
        Object.entries(truckLinks).forEach(([truckId, deviceId]) => {
          const reading = latestByDevice[deviceId];
          if (reading) {
            healthMap[truckId] = {
              ...reading,
              alertStatus: getAlertStatus(reading.healthScore)
            };
          } else {
            healthMap[truckId] = {
              alertStatus: 'Offline',
              healthScore: 0,
              deviceId,
              createdAt: undefined
            };
          }
        });

        setDeviceHealthMap(healthMap);
      } catch (error) {
        console.error('Unable to load device health data:', error);
      }
    };

    loadDeviceHealth();
  }, [state.trucks]);


  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedTrucks(filteredTrucks.slice(startIndex, endIndex));
  }, [filteredTrucks, currentPage, itemsPerPage]);

  const handleTruckSaved = (truck: Truck) => {
    setShowTruckForm(false);
    setEditingTruck(undefined);

  };

  const handleAddTruck = () => {
    setEditingTruck(undefined);
    setShowTruckForm(true);
  };

  const handleEditTruck = (truck: Truck) => {
    setEditingTruck(truck);
    setShowTruckForm(true);
  };

  const handleViewTruckDetails = (truck: Truck) => {
    navigate(`/trucks/${truck.id}`);
  };

  const handleDeleteTruck = async (truck: Truck) => {
    if (window.confirm(`Are you sure you want to delete ${truck.licensePlate}? This action cannot be undone.`)) {
      try {
        await deleteTruck(truck.id);
        setSelectedTrucks(current => current.filter(id => id !== truck.id));

      } catch (error) {
        console.error('Error deleting truck:', error);
        alert('Failed to delete truck. Please try again.');
      }
    }
  };


  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  setSelectedTrucks([]); 
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
  setCurrentPage(1); 
    setSelectedTrucks([]);
  };

    const handleBulkStatusUpdate = async (status: TruckStatus) => {
    if (selectedTrucks.length === 0) return;
    
    try {

      await Promise.all(selectedTrucks.map((truckId: string) => 
        updateTruck(truckId, { status })
      ));
      
      setSelectedTrucks([]);

    } catch (error) {
      console.error('Error updating truck statuses:', error);
    }
  };

  const handleSelectTruck = (truckId: string) => {
    setSelectedTrucks((prev: string[]) => 
      prev.includes(truckId) 
        ? prev.filter((id: string) => id !== truckId)
        : [...prev, truckId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTrucks.length === filteredTrucks.length) {
      setSelectedTrucks([]);
    } else {
      setSelectedTrucks(filteredTrucks.map((truck: Truck) => truck.id));
    }
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'VIN', 'License Plate', 'Make', 'Model', 'Year', getMileageCsvHeader(distanceUnit), 'Status', 'Nickname'];
    const rows = filteredTrucks.map((truck: Truck) => [
      truck.id,
      truck.vin,
      truck.licensePlate,
      truck.make,
      truck.model,
      truck.year,
      Math.round(toDisplayDistance(truck.mileage, distanceUnit)),
      truck.status,
      truck.nickname || ''
    ]);

    downloadTextFile(
      `trucks-export-${new Date().toISOString().split('T')[0]}.csv`,
      toCsv(headers, rows),
      'text/csv;charset=utf-8;'
    );
    setImportFeedback({
      tone: 'success',
      text: `Exported ${filteredTrucks.length} vehicle record${filteredTrucks.length === 1 ? '' : 's'}.`
    });
  };

  const handleImportCSV = () => {
    importInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const { records } = parseCsvRecords(text);
      const errors: string[] = [];
      const knownTrucks = [...state.trucks];
      const actor = state.currentUser?.id || 'import';
      const now = new Date();
      let added = 0;
      let updated = 0;

      if (records.length === 0) {
        setImportFeedback({ tone: 'warning', text: 'No vehicle rows were found in that CSV.' });
        return;
      }

      for (let index = 0; index < records.length; index += 1) {
        const record = records[index];
        const rowNumber = index + 2;
        const id = getCsvCell(record, ['ID', 'Truck ID', 'Vehicle ID']);
        const vin = getCsvCell(record, ['VIN', 'Vehicle Identification Number']).toUpperCase();
        const licensePlate = getCsvCell(record, ['License Plate', 'Plate', 'Tag', 'Registration']);
        const make = getCsvCell(record, ['Make', 'Manufacturer']);
        const model = getCsvCell(record, ['Model']);
        const nickname = getCsvCell(record, ['Nickname', 'Name', 'Vehicle Name']);
        const status = normalizeTruckStatus(getCsvCell(record, ['Status', 'Vehicle Status']));
        const year = Math.trunc(parseNumber(getCsvCell(record, ['Year', 'Model Year']), 0));
        const mileage = readMileageFromCsvRecord(record, distanceUnit);

        const missingFields = [
          !vin && 'VIN',
          !licensePlate && 'License Plate',
          !make && 'Make',
          !model && 'Model',
          !year && 'Year'
        ].filter(Boolean);

        if (missingFields.length > 0) {
          errors.push(`Row ${rowNumber}: missing ${missingFields.join(', ')}.`);
          continue;
        }

        const existingTruck = knownTrucks.find(truck =>
          (id && truck.id === id) ||
          (vin && truck.vin.toLowerCase() === vin.toLowerCase()) ||
          (licensePlate && truck.licensePlate.toLowerCase() === licensePlate.toLowerCase())
        );

        if (existingTruck) {
          const updates: Partial<Truck> = {
            vin,
            licensePlate,
            make,
            model,
            year,
            mileage,
            nickname,
            status,
            updatedAt: now,
            updatedBy: actor
          };

          await updateTruck(existingTruck.id, updates);
          Object.assign(existingTruck, updates);
          updated += 1;
          continue;
        }

        const newTruck: Truck = {
          id: id || makeImportId('truck', rowNumber),
          vin,
          licensePlate,
          make,
          model,
          year,
          mileage,
          nickname,
          status,
          customFields: {},
          createdAt: now,
          updatedAt: now,
          createdBy: actor,
          updatedBy: actor
        };

        await addTruck(newTruck);
        knownTrucks.unshift(newTruck);
        added += 1;
      }

      const skipped = errors.length;
      setImportFeedback({
        tone: skipped > 0 ? (added + updated > 0 ? 'warning' : 'danger') : 'success',
        text: `Import complete: ${added} added, ${updated} updated, ${skipped} skipped.`,
        details: errors.slice(0, 8)
      });
    } catch (error) {
      setImportFeedback({
        tone: 'danger',
        text: error instanceof Error ? error.message : 'Vehicle import failed. Please check the CSV and try again.'
      });
    } finally {
      event.target.value = '';
    }
  };

  const getStatusBadgeClass = (status: TruckStatus) => {
    switch (status) {
      case 'In Service': return 'status-badge status-active';
      case 'Out for Repair': return 'status-badge status-maintenance';
      case 'Needs Attention': return 'status-badge status-warning';
      case 'Retired': return 'status-badge status-inactive';
      default: return 'status-badge';
    }
  };

  const getAlertStatus = (healthScore: number) => {
    if (healthScore >= 80) return 'Normal';
    if (healthScore >= 60) return 'Warning';
    return 'Critical';
  };

  const renderHealthBadge = (truck: Truck) => {
    const health = deviceHealthMap[truck.id];
    const hasLink = Boolean(linkedDeviceMap[truck.id]);

    if (!hasLink) {
      return null;
    }

    const status = health?.alertStatus || 'Offline';
    return (
      <span className={`health-badge ${status.toLowerCase()}`}>
        {status}
      </span>
    );
  };

  const renderCardView = () => (
    <div className="trucks-grid">
      {paginatedTrucks.map((truck: Truck) => (
        <div key={truck.id} className="truck-card">
          <div className="truck-card-header">
            <div className="truck-info">
              <h3 
                onClick={() => handleViewTruckDetails(truck)}
                className="truck-title-clickable"
                title="View truck details"
              >
                {truck.make} {truck.model}
              </h3>
              <span className="truck-license">{truck.licensePlate}</span>
              {renderHealthBadge(truck) && (
                <div style={{ marginTop: '8px' }}>
                  {renderHealthBadge(truck)}
                </div>
              )}
            </div>
            <div className="truck-actions">
              <input
                type="checkbox"
                checked={selectedTrucks.includes(truck.id)}
                onChange={() => handleSelectTruck(truck.id)}
                className="truck-checkbox"
              />
              
              <select
                value={truck.status}
                onChange={e => {
                  const newStatus = e.target.value;
                  updateTruck(truck.id, { status: newStatus as TruckStatus });
                  setTimeout(() => {
                    const note = prompt(`Add a note for status '${newStatus}' (optional):`, '');
                    if (note !== null && note.trim() !== '') {
                      const customFields = { ...(truck.customFields || {}), statusNote: note };
                      updateTruck(truck.id, { customFields });
                    }
                  }, 100);
                }}
                className="action-btn status-dropdown status-dropdown-shrink"
                title="Change Status"
              >
                <option value="In Service">In Service</option>
                <option value="Needs Attention">Needs Attention</option>
                <option value="Out for Repair">Out for Repair</option>
                <option value="Retired">Retired</option>
              </select>
              <button 
                onClick={() => handleViewTruckDetails(truck)} 
                className="action-btn view"
                title="View Details"
              >
                <Eye size={16} />
              </button>
              <div className="edit-delete-actions">
                <button onClick={() => handleEditTruck(truck)} className="action-btn edit">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDeleteTruck(truck)} className="action-btn delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
          
          <div className="truck-details">
            <div className="detail-row">
              <span className="label">Year:</span>
              <span className="value">{truck.year}</span>
            </div>
            <div className="detail-row">
              <span className="label">Mileage:</span>
              <span className="value">{formatDistance(truck.mileage, { unit: distanceUnit, longUnit: true })}</span>
            </div>
            <div className="detail-row">
              <span className="label">Nickname:</span>
              <span className="value">{truck.nickname || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className={getStatusBadgeClass(truck.status)}>{truck.status}</span>
            </div>
            {truck.customFields && Object.keys(truck.customFields).length > 0 && (
              <div className="custom-fields">
                {Object.entries(truck.customFields).map(([key, value]) => (
                  <div key={key} className="detail-row">
                    <span className="label">{key}:</span>
                    <span className="value">{String(value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderTableView = () => (
    <div className="trucks-table-container">
      <table className="trucks-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectedTrucks.length === filteredTrucks.length && filteredTrucks.length > 0}
                onChange={handleSelectAll}
              />
            </th>
            <th>License Plate</th>
            <th>Make & Model</th>
            <th>Year</th>
            <th>Mileage ({getDistanceUnitLabel(distanceUnit)})</th>
            <th>Status</th>
            <th>Monitoring</th>
            <th>Nickname</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedTrucks.map((truck: Truck) => (
            <tr key={truck.id}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedTrucks.includes(truck.id)}
                  onChange={() => handleSelectTruck(truck.id)}
                />
              </td>
              <td className="license-plate">{truck.licensePlate}</td>
              <td>{truck.make} {truck.model}</td>
              <td>{truck.year}</td>
              <td>{formatDistance(truck.mileage, { unit: distanceUnit, includeUnit: false })}</td>
              <td>
                <select
                  value={truck.status}
                  onChange={e => {
                    const newStatus = e.target.value;
                    updateTruck(truck.id, { status: newStatus as TruckStatus });
                    setTimeout(() => {
                      const note = prompt(`Add a note for status '${newStatus}' (optional):`, '');
                      if (note !== null && note.trim() !== '') {
                        const customFields = { ...(truck.customFields || {}), statusNote: note };
                        updateTruck(truck.id, { customFields });
                      }
                    }, 100);
                  }}
                  className="action-btn status-dropdown status-dropdown-shrink"
                  title="Change Status"
                >
                  <option value="In Service">In Service</option>
                  <option value="Needs Attention">Needs Attention</option>
                  <option value="Out for Repair">Out for Repair</option>
                  <option value="Retired">Retired</option>
                </select>
              </td>
              <td>{renderHealthBadge(truck) || 'No device linked'}</td>
              <td>{truck.nickname || '-'}</td>
              <td>
                <div className="table-actions">
                  <button 
                    onClick={() => handleViewTruckDetails(truck)} 
                    className="action-btn view" 
                    title="View Details"
                  >
                    <Eye size={16} />
                  </button>
                  <button onClick={() => handleEditTruck(truck)} className="action-btn edit" title="Edit">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDeleteTruck(truck)} className="action-btn delete" title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="trucks-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Fleet Trucks</h1>
          <p>Manage your fleet vehicles and track their status</p>
        </div>
        <div className="header-actions">
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportFile}
            style={{ display: 'none' }}
          />
          <button onClick={handleExportCSV} className="btn-secondary">
            <Download size={16} />
            Export CSV
          </button>
          <button onClick={handleImportCSV} className="btn-secondary">
            <Upload size={16} />
            Import
          </button>
          <button 
            onClick={handleAddTruck}
            className="btn-primary"
          >
            <Plus size={16} />
            Add Truck
          </button>
        </div>
      </div>

      {importFeedback && (
        <div className={`import-feedback ${importFeedback.tone}`} role="status">
          <strong>{importFeedback.text}</strong>
          {importFeedback.details && importFeedback.details.length > 0 && (
            <ul>
              {importFeedback.details.map(detail => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <SearchAndFilter
        entityType="trucks"
        onSearch={setSearchQuery}
        onFilter={setFilters}
        onClear={() => {
          setSearchQuery('');
          setFilters({});
        }}
        currentFilters={filters}
      />

      <div className="content-header">
        <div className="content-info">
          <span className="results-count">
            {filteredTrucks.length} of {trucks.length} trucks
          </span>
          {selectedTrucks.length > 0 && (
            <div className="bulk-actions">
              <span>{selectedTrucks.length} selected</span>
              <button 
                onClick={() => handleBulkStatusUpdate('In Service')}
                className="bulk-action-btn"
              >
                Mark In Service
              </button>
              <button 
                onClick={() => handleBulkStatusUpdate('Out for Repair')}
                className="bulk-action-btn"
              >
                Mark Out for Repair
              </button>
              <button 
                onClick={() => handleBulkStatusUpdate('Needs Attention')}
                className="bulk-action-btn"
              >
                Mark Needs Attention
              </button>
            </div>
          )}
        </div>
        <div className="view-controls">
          <button 
            onClick={() => setViewMode('cards')}
            className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
          >
            Cards
          </button>
          <button 
            onClick={() => setViewMode('table')}
            className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
          >
            Table
          </button>
        </div>
      </div>

      
      {filteredTrucks.length > 0 && (
        <div className="pagination-controls">
          <div className="pagination-info">
            <span>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredTrucks.length)} of {filteredTrucks.length} trucks
            </span>
            <div className="items-per-page">
              <label>Show:</label>
              <select 
                value={itemsPerPage} 
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
              </select>
              <span>per page</span>
            </div>
          </div>
          
          {totalPages > 1 && (
            <div className="pagination-buttons">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              ))}
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {filteredTrucks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <AlertTriangle size={48} />
          </div>
          <h3>No trucks found</h3>
          <p>
            {searchQuery || Object.keys(filters).length > 0 
              ? 'No trucks match your current search and filters.'
              : 'Get started by adding your first truck to the fleet.'
            }
          </p>
          {(!searchQuery && Object.keys(filters).length === 0) && (
            <button 
              onClick={handleAddTruck}
              className="btn-primary"
            >
              <Plus size={16} />
              Add Your First Truck
            </button>
          )}
        </div>
      ) : (
        viewMode === 'cards' ? renderCardView() : renderTableView()
      )}

      {showTruckForm && (
        <TruckForm
          truck={editingTruck}
          onSuccess={handleTruckSaved}
          onCancel={() => {
            setShowTruckForm(false);
            setEditingTruck(undefined);
          }}
        />
      )}
    </div>
  );
};

export default EnhancedTrucksPage;
